import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  PackageOpen, 
  Plus, 
  Send, 
  FileText, 
  Search, 
  CheckCircle, 
  X, 
  ChevronRight, 
  PackageCheck, 
  Trash2, 
  Package2, 
  Layers, 
  Tag, 
  PenTool, 
  Building2, 
  AlertTriangle,
  Loader2,
  CheckSquare,
  Square,
  Clock,
  Timer,
  FileDown,
  Printer,
  Ban,
  ChevronDown,
  Pencil,
  Check,
  Bot
} from 'lucide-react';
import { toast } from 'sonner';
import { useSession } from '@/lib/SessionContext';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useKurs } from '@/hooks/useKurs';
import { getShowPriceAccess } from '@/lib/AuthSession';
import { exportToPDF } from '@/utils/exportPDF';
import SearchableSelect from '@/components/ui/SearchableSelect';
import AsaAssistant from '@/components/shared/AsaAssistant';

const fmt = (n) => new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(n || 0);
const fmtDecimal = (n) => {
  if (n === null || n === undefined) return '0';
  const num = Number(n);
  if (isNaN(num)) return '0';
  if (Number.isInteger(num)) return num.toString();
  return num.toLocaleString('id-ID', { minimumFractionDigits: 1, maximumFractionDigits: 3 });
};

const calculateLeadTime = (startDate, endDate = null) => {
  if (!startDate) return null;
  const start = new Date(startDate);
  if (isNaN(start.getTime())) return null;
  const end = endDate ? new Date(endDate) : new Date();
  if (isNaN(end.getTime())) return null;

  const diffMs = Math.max(0, end - start);
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);
  const remainingHours = diffHours % 24;

  if (diffDays === 0 && remainingHours === 0) return 'Baru saja';
  if (diffDays === 0) return `${remainingHours} Jam`;
  if (remainingHours === 0) return `${diffDays} Hari`;
  return `${diffDays} Hari ${remainingHours} Jam`;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Harus Dibeli': return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'Dalam Permintaan': return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'Menunggu Pembayaran': return 'bg-indigo-100 text-indigo-800 border-indigo-300';
    case 'Sudah Dibayar': return 'bg-purple-100 text-purple-800 border-purple-300';
    case 'Dalam Pengiriman': return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    case 'Sudah Datang': return 'bg-slate-100 text-slate-800 border-slate-300';
    default: return 'bg-slate-100 text-slate-800 border-slate-300';
  }
};

const STATUS_LIST = [
  'Harus Dibeli',
  'Dalam Permintaan',
  'Menunggu Pembayaran',
  'Sudah Dibayar',
  'Dalam Pengiriman',
  'Sudah Datang'
];

const isRequestMatchingItem = (req, itemId, itemType) => {
  if (!req) return false;
  if (req.batch_items) {
    let items = [];
    try {
      items = typeof req.batch_items === 'string' ? JSON.parse(req.batch_items) : req.batch_items;
    } catch (e) {}
    if (Array.isArray(items)) {
      return items.some(bi => String(bi.id) === String(itemId) && String(bi.type) === String(itemType));
    }
  }
  return String(req.material_id) === String(itemId);
};

const getItemKey = (item) => {
  return item._ticket ? `ticket_${item._ticket.id}` : `${item._type || item.type || 'item'}_${item.id || item.nama || item.name}`;
};

export default function PurchasingLowStock() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAsa, setShowAsa] = useState(true);
  const { member } = useSession();
  const showPrice = getShowPriceAccess(member, "purchasing_items") || getShowPriceAccess(member, "portal_purchasing");

  // ── Batch Selection State ──
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  // ── Ignored Items State ──
  const [ignoredKeys, setIgnoredKeys] = useState(() => {
    try {
      const saved = localStorage.getItem('sde_purchasing_ignored_low_stock');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (e) {
      return new Set();
    }
  });

  // ── Qty Input State ──
  const [qtyInputs, setQtyInputs] = useState({});
  const [batchFillQty, setBatchFillQty] = useState('');

  // ── Pengajuan Modal State ──
  const [showPengajuanModal, setShowPengajuanModal] = useState(false);
  const [pengajuanItem, setPengajuanItem] = useState(null);
  const [isSubmittingPengajuan, setIsSubmittingPengajuan] = useState(false);
  const [pengajuanBank, setPengajuanBank] = useState('BCA');
  const [pengajuanNoRek, setPengajuanNoRek] = useState('');
  const [pengajuanNamaRek, setPengajuanNamaRek] = useState('');
  const [pengajuanNamaSales, setPengajuanNamaSales] = useState('');
  const [pengajuanAdaFakturPajak, setPengajuanAdaFakturPajak] = useState('Tidak');
  const [pengajuanNamaPerusahaan, setPengajuanNamaPerusahaan] = useState('');
  const [pengajuanNPWP, setPengajuanNPWP] = useState('');
  const [pengajuanSupplierPhone, setPengajuanSupplierPhone] = useState('');
  const [pengajuanSupplierAddress, setPengajuanSupplierAddress] = useState('');
  const [pengajuanMetode, setPengajuanMetode] = useState('Cash');
  const [pengajuanTempo, setPengajuanTempo] = useState('');
  const [pengajuanOngkirMode, setPengajuanOngkirMode] = useState('transfer');
  const [pengajuanOngkir, setPengajuanOngkir] = useState('');
  const [pengajuanCatatan, setPengajuanCatatan] = useState('');
  
  const [pengajuanPemesanNama, setPengajuanPemesanNama] = useState('');
  const [pengajuanPemesanNPWP, setPengajuanPemesanNPWP] = useState('');
  const [pengajuanPemesanAlamat, setPengajuanPemesanAlamat] = useState('');
  const [pengajuanPemesanPIC, setPengajuanPemesanPIC] = useState('');
  const [pengajuanPemesanTelp, setPengajuanPemesanTelp] = useState('');

  const [supplierSearch, setSupplierSearch] = useState('');
  const [pemesanSearch, setPemesanSearch] = useState('');

  // ── Manual Item Modal ──
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualCategory, setManualCategory] = useState('');
  const [manualVendor, setManualVendor] = useState('');
  const [manualItem, setManualItem] = useState('');
  const [manualQty, setManualQty] = useState('');
  const [isSubmittingManual, setIsSubmittingManual] = useState(false);

  // ── Source & Supplier Filter State ──
  const [selectedSupplierFilter, setSelectedSupplierFilter] = useState('all');
  const [selectedSourceFilter, setSelectedSourceFilter] = useState('all');
  const { kurs, kursEuro } = useKurs();
  const [mainSearchQuery, setMainSearchQuery] = useState('');

  // ── Progress Modal State ──
  const [selectedProgressItem, setSelectedProgressItem] = useState(null);

  // ── Pagination State ──
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ── Fetch Master Materials ──
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });
  const { data: bahanCairList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });

  // ── Fetch Purchasing Tickets & Suppliers & Requests ──
  const { data: tickets = [] } = useQuery({
    queryKey: ['purchasing-tickets'],
    queryFn: () => base44.entities.PurchasingTicket.list()
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ['supplier-database'],
    queryFn: async () => {
      try {
        const res = await base44.entities.SupplierDatabase?.list();
        return res || [];
      } catch (err) {
        return [];
      }
    }
  });

  const { data: requests = [] } = useQuery({
    queryKey: ['purchasing-requests'],
    queryFn: () => base44.entities.PurchasingRequest.list('-created_date')
  });

  const { data: dataPemesanList = [] } = useQuery({
    queryKey: ['data-pemesan'],
    queryFn: async () => {
      try {
        const res = await base44.entities.DataPemesan?.list();
        return res || [];
      } catch (err) {
        return [];
      }
    }
  });

  const updateTicketMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.PurchasingTicket.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] })
  });

  const deleteTicketMut = useMutation({
    mutationFn: (id) => base44.entities.PurchasingTicket.hardDelete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] })
  });

  // ── Fetch Active POs & Production Orders (to exclude items already needed by PO) ──
  const { clientPOs = [] } = useClientPOs();
  const { data: productionOrders = [] } = useQuery({
    queryKey: ['production-orders'],
    queryFn: () => base44.entities.ProductionOrder.list()
  });

  // ── DETECT ITEMS NEEDED BY ACTIVE POS (EXCLUDE THEM FROM THIS BUFFER STOCK PAGE) ──
  const neededByActivePOs = useMemo(() => {
    const neededIds = new Set();
    const neededNames = new Set();

    const activeProdOrders = (productionOrders || []).filter(po => {
      if (po.deleted_at) return false;
      const st = String(po.status || '').toLowerCase().trim();
      return !['done', 'selesai', 'shipped', 'dikirim', 'batal', 'cancelled'].includes(st);
    });

    activeProdOrders.forEach(po => {
      const st = String(po.status || '').toLowerCase().trim();
      const isRacikDone = ['diracik', 'racik', 'mixing', 'maserasi', 'menunggu_filling', 'filling', 'menunggu_packing', 'packing', 'siap_kirim'].includes(st) ||
                          po.task_stock_deducted?.racik === true || 
                          po.is_racik_done === true;
      const isFillingDone = ['menunggu_packing', 'packing', 'siap_kirim'].includes(st) || 
                            po.task_stock_deducted?.filling === true;
      const isPackingDone = ['siap_kirim'].includes(st) || 
                            po.task_stock_deducted?.packaging === true;

      (po.components || []).forEach(c => {
        const cat = String(c.category || '').toLowerCase();
        let needed = true;
        if (['bibit', 'bahan_cair'].includes(cat) && isRacikDone) needed = false;
        if (['botol', 'tutup', 'spray', 'fullset'].includes(cat) && isFillingDone) needed = false;
        if (['item-tambahan', 'tambahan'].includes(cat) && isPackingDone) needed = false;

        if (needed) {
          if (c.inventory_item_id) {
            const clean = String(c.inventory_item_id).replace(/^(botol-|tutup-|spray-|bahan-|tambahan-|fullset-)/, '');
            neededIds.add(clean);
            neededIds.add(String(c.inventory_item_id));
          }
          const cName = String(c.item_name || c.name || '').toLowerCase().trim();
          if (cName) neededNames.add(cName);
        }
      });

      if (!isRacikDone) {
        (po.products || []).forEach(prod => {
          (prod.racikan_digunakan || prod.racikan || []).forEach(r => {
            const n = String(r.nama_bahan || r.nama || '').toLowerCase().trim();
            if (n) neededNames.add(n);
          });
        });
      }
    });

    const activeClientPOs = (clientPOs || []).filter(po => {
      const st = String(po.statusProduksi || '').toUpperCase().trim();
      return !['PESANAN SELESAI', 'SELESAI', 'BATAL', 'SUDAH SAMPAI', 'DIKIRIM', 'DIKIRIM SEBAGIAN'].includes(st);
    });

    activeClientPOs.forEach(po => {
      (po.components || []).forEach(c => {
        if (c.inventory_item_id) {
          const clean = String(c.inventory_item_id).replace(/^(botol-|tutup-|spray-|bahan-|tambahan-|fullset-)/, '');
          neededIds.add(clean);
          neededIds.add(String(c.inventory_item_id));
        }
        const cName = String(c.item_name || c.name || '').toLowerCase().trim();
        if (cName) neededNames.add(cName);
      });
    });

    return { neededIds, neededNames };
  }, [productionOrders, clientPOs]);

  // ── CALCULATE LOW STOCK ITEMS (THREAT LEVEL / REORDER POINT) ──
  const lowStockItems = useMemo(() => {
    const results = [];
    const processedItemKeys = new Set();

    const isItemNeededByPO = (item) => {
      if (!item) return false;
      const idStr = String(item.id || '');
      const namaStr = String(item.nama || '').toLowerCase().trim();
      if (neededByActivePOs.neededIds.has(idStr)) return true;
      if (namaStr && neededByActivePOs.neededNames.has(namaStr)) return true;
      return false;
    };

    // 1. Periksa Bahan Cair: Alkohol batas 20L, Bahan Cair lain batas 1kg/1L
    (bahanCairList || []).forEach(b => {
      if (b.deleted_at) return;
      const isClientTitipan = b.dari_client === true || b.vendor === 'Client' || (b.vendor || '').toLowerCase().includes('client') || (b.nama || '').toLowerCase().includes('titipan');
      if (isClientTitipan) return;
      if (isItemNeededByPO(b)) return; // Eksklusif: Jangan tampilkan jika sedang dibutuhkan PO!

      const stok = Number(b.stok || 0);
      const isAlkohol = String(b.kategori || '').toLowerCase() === 'alkohol' || String(b.nama || '').toLowerCase().includes('alkohol') || String(b.nama || '').toLowerCase().includes('superfine');
      const threshold = isAlkohol ? 20 : 1;

      if (stok < threshold) {
        const activeTicket = tickets.find(t => String(t.material_id) === String(b.id) && t.material_type === 'bahan-cair' && t.status === 'Harus Dibeli');
        const deficitQty = activeTicket ? activeTicket.qty_to_buy : Math.max(0, threshold - stok);
        const itemKey = `bahan-cair_${b.id}`;

        results.push({
          ...b,
          _reqQty: 0,
          _threshold: threshold,
          _deficitQty: deficitQty > 0 ? Number(deficitQty.toFixed(2)) : (threshold > stok ? Number((threshold - stok).toFixed(2)) : 1),
          _type: 'bahan-cair',
          _ticket: activeTicket || null,
          _price: b.harga_rupiah || b.harga_beli || 0,
          _isLowStock: true,
          satuan: b.satuan || (isAlkohol ? 'liter' : 'kg'),
          vendor: activeTicket?.vendor || b.vendor || b.supplier || ''
        });
        processedItemKeys.add(itemKey);
      }
    });

    // 2. Periksa Packaging: Botol, Tutup, Spray, Fullset batas 100 pcs
    const checkPackaging = (list, type) => {
      (list || []).forEach(p => {
        if (p.deleted_at || p.status === 'nonaktif') return;
        const isClientTitipan = p.dari_client === true || p.vendor === 'Client' || (p.vendor || '').toLowerCase().includes('client') || (p.nama || '').toLowerCase().includes('titipan');
        if (isClientTitipan) return;
        if (isItemNeededByPO(p)) return; // Eksklusif: Jangan tampilkan jika sedang dibutuhkan PO!

        const stok = Number(p.stok || 0);
        const threshold = 100;

        if (stok < threshold) {
          const activeTicket = tickets.find(t => String(t.material_id) === String(p.id) && t.material_type === type && t.status === 'Harus Dibeli');
          const deficitQty = activeTicket ? activeTicket.qty_to_buy : Math.max(0, threshold - stok);
          const itemKey = `${type}_${p.id}`;

          results.push({
            ...p,
            _reqQty: 0,
            _threshold: threshold,
            _deficitQty: deficitQty > 0 ? Number(deficitQty.toFixed(0)) : (threshold > stok ? (threshold - stok) : 100),
            _type: type,
            _ticket: activeTicket || null,
            _price: p.harga_rupiah || p.harga_beli || 0,
            _isLowStock: true,
            satuan: p.satuan || 'pcs',
            vendor: activeTicket?.vendor || p.vendor || p.supplier || ''
          });
          processedItemKeys.add(itemKey);
        }
      });
    };

    checkPackaging(botolList, 'botol');
    checkPackaging(tutupList, 'tutup');
    checkPackaging(sprayList, 'spray');
    checkPackaging(fullsetList, 'fullset');

    // 3. Kumpulkan Tiket Manual yang berstatus 'Harus Dibeli' dan dibuat khusus di halaman Item Perlu Beli (source_page === 'low_stock')
    (tickets || []).forEach(ticket => {
      if (!ticket || ticket.status !== 'Harus Dibeli' || ticket.source_page !== 'low_stock') return;
      const type = ticket.material_type || 'bahan-cair';
      const matId = String(ticket.material_id);
      const itemKey = `${type}_${matId}`;
      if (processedItemKeys.has(itemKey)) return;

      let masterItem = null;
      if (type === 'botol') masterItem = botolList.find(i => String(i.id) === matId);
      else if (type === 'tutup') masterItem = tutupList.find(i => String(i.id) === matId);
      else if (type === 'spray') masterItem = sprayList.find(i => String(i.id) === matId);
      else if (type === 'fullset') masterItem = fullsetList.find(i => String(i.id) === matId);
      else if (type === 'bahan-cair') masterItem = bahanCairList.find(i => String(i.id) === matId);

      if (masterItem) {
        if (isItemNeededByPO(masterItem)) return; // Eksklusif: Jangan tampilkan jika sedang dibutuhkan PO!
        masterItem._type = type;
        const price = masterItem.harga_rupiah || masterItem.harga_beli || 0;
        const buyQty = ticket.qty_to_buy || 0;
        if (buyQty > 0) {
          results.push({
            ...masterItem,
            _reqQty: 0,
            _deficitQty: buyQty,
            _type: type,
            _ticket: ticket,
            _price: price,
            _isManualInput: true,
            satuan: masterItem.satuan || 'pcs',
            vendor: ticket.vendor || masterItem.vendor || masterItem.supplier || ''
          });
          processedItemKeys.add(itemKey);
        }
      }
    });

    return results
      .filter(item => {
        const key = getItemKey(item);
        if (ignoredKeys.has(key)) return false;
        return true;
      })
      .sort((a, b) => {
        const stokA = Number(a.stok || 0);
        const stokB = Number(b.stok || 0);
        if (stokA <= 0 && stokB > 0) return -1;
        if (stokA > 0 && stokB <= 0) return 1;
        return stokA - stokB;
      });
  }, [botolList, tutupList, sprayList, fullsetList, bahanCairList, tickets, ignoredKeys, neededByActivePOs]);

  // ── GROUP BY SUPPLIER ──
  const supplierGrouped = useMemo(() => {
    const groups = {};
    lowStockItems.forEach(item => {
      const itemVendor = (item.vendor || item.supplier || '').trim();
      let matchedSupplier = null;
      if (itemVendor) {
        matchedSupplier = suppliers.find(s => {
          let match = false;
          if (s.handled_vendors && Array.isArray(s.handled_vendors) && s.handled_vendors.length > 0) {
            match = s.handled_vendors.some(v => String(v || '').toLowerCase() === String(itemVendor || '').toLowerCase());
          }
          if (!match) {
            match = String(s.nama_sales || '').toLowerCase() === String(itemVendor || '').toLowerCase();
          }
          return match;
        });
      }
      const groupKey = matchedSupplier ? `supplier_${matchedSupplier.id}` : (itemVendor ? `vendor_${itemVendor}` : 'no_vendor');
      const groupLabel = matchedSupplier ? matchedSupplier.nama_sales : (itemVendor || 'Tanpa Vendor');
      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          label: groupLabel,
          supplier: matchedSupplier || null,
          vendorName: itemVendor,
          items: []
        };
      }
      groups[groupKey].items.push(item);
    });

    return Object.values(groups).sort((a, b) => {
      if (a.supplier && !b.supplier) return -1;
      if (!a.supplier && b.supplier) return 1;
      if (a.key === 'no_vendor') return 1;
      if (b.key === 'no_vendor') return -1;
      return b.items.length - a.items.length;
    });
  }, [lowStockItems, suppliers]);

  const sourceCounts = useMemo(() => {
    let lowStock = 0;
    let manual = 0;
    lowStockItems.forEach(item => {
      if (item._isManualInput || item._ticket?.is_manual) manual++;
      else lowStock++;
    });
    return { lowStock, manual, total: lowStockItems.length };
  }, [lowStockItems]);

  const filteredSupplierGrouped = useMemo(() => {
    let filtered = supplierGrouped;
    if (selectedSupplierFilter !== 'all') {
      filtered = filtered.filter(g => g.key === selectedSupplierFilter);
    }
    if (selectedSourceFilter !== 'all') {
      filtered = filtered.map(group => {
        const matchingItems = group.items.filter(item => {
          const isManual = item._isManualInput || item._ticket?.is_manual;
          if (selectedSourceFilter === 'low_stock') return !isManual;
          if (selectedSourceFilter === 'manual') return isManual;
          return true;
        });
        return { ...group, items: matchingItems };
      }).filter(group => group.items.length > 0);
    }
    if (mainSearchQuery) {
      const q = mainSearchQuery.toLowerCase().trim();
      filtered = filtered.map(group => {
        const matchingItems = group.items.filter(item => {
          const nameMatch = (item.nama || item.name || '').toLowerCase().includes(q);
          const catMatch = (item.kategori || item._type || '').toLowerCase().includes(q);
          const vendorMatch = (item.vendor || item.supplier || group.label || group.vendorName || '').toLowerCase().includes(q);
          const codeMatch = (item.kode || '').toLowerCase().includes(q);
          const brandMatch = (item._brands || []).some(b => String(b).toLowerCase().includes(q)) || 
                             (item._brandNames || []).some(b => String(b).toLowerCase().includes(q));
          return nameMatch || catMatch || vendorMatch || codeMatch || brandMatch;
        });
        return { ...group, items: matchingItems };
      }).filter(group => group.items.length > 0);
    }
    return filtered;
  }, [supplierGrouped, selectedSupplierFilter, selectedSourceFilter, mainSearchQuery]);

  // ── Qty Input Helpers ──
  const getQtyForItem = (item) => {
    const key = getItemKey(item);
    const input = qtyInputs[key];
    if (input) return input;
    return { qty: item._deficitQty, confirmed: true };
  };

  const setQtyForItem = (item, qty) => {
    const key = getItemKey(item);
    setQtyInputs(prev => ({ ...prev, [key]: { qty: qty, confirmed: false } }));
  };

  const editQtyForItem = (item) => {
    const key = getItemKey(item);
    setQtyInputs(prev => ({
      ...prev,
      [key]: { qty: prev[key]?.qty ?? item._deficitQty, confirmed: false }
    }));
  };

  const confirmQtyForItem = async (item) => {
    const key = getItemKey(item);
    const current = getQtyForItem(item);
    const parsedQty = parseFloat(String(current.qty).replace(',', '.')) || 0;
    if (parsedQty <= 0) {
      toast.error('Jumlah beli harus lebih dari 0');
      return;
    }

    try {
      if (item._ticket) {
        await updateTicketMut.mutateAsync({
          id: item._ticket.id,
          data: { qty_to_buy: parsedQty }
        });
      } else {
        await base44.entities.PurchasingTicket.create({
          material_id: item.id,
          material_type: item._type,
          material_name: item.nama,
          qty_to_buy: parsedQty,
          status: 'Harus Dibeli',
          vendor: item.vendor || null
        });
        queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] });
      }
      setQtyInputs(prev => ({ ...prev, [key]: { qty: parsedQty, confirmed: true } }));
      toast.success('Jumlah beli berhasil diperbarui');
    } catch (err) {
      toast.error('Gagal memperbarui jumlah beli: ' + (err.message || 'Error'));
    }
  };

  const handleIgnoreItem = (item) => {
    const key = getItemKey(item);
    const next = new Set(ignoredKeys);
    next.add(key);
    setIgnoredKeys(next);
    try {
      localStorage.setItem('sde_purchasing_ignored_low_stock', JSON.stringify(Array.from(next)));
    } catch(e) {}
    toast.success('Item berhasil diabaikan dari daftar');
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      if (item._ticket) {
        await updateTicketMut.mutateAsync({
          id: item._ticket.id,
          data: { status: newStatus }
        });
      } else {
        await base44.entities.PurchasingTicket.create({
          material_id: item.id,
          material_type: item._type,
          material_name: item.nama,
          qty_to_buy: getQtyForItem(item).qty || item._deficitQty || 1,
          status: newStatus,
          vendor: item.vendor || null
        });
        queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] });
      }
      toast.success(`Status berhasil diubah ke ${newStatus}`);
    } catch (err) {
      toast.error('Gagal mengubah status: ' + (err.message || 'Error'));
    }
  };

  // ── Batch Actions ──
  const toggleSelection = (item) => {
    const key = getItemKey(item);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === lowStockItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(lowStockItems.map(d => getItemKey(d))));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setIsSelectionMode(false);
  };

  const handleBatchFillQty = async () => {
    const parsed = parseFloat(String(batchFillQty).replace(',', '.')) || 0;
    if (parsed <= 0) {
      toast.error('Jumlah pengisian harus lebih dari 0');
      return;
    }
    const selectedItems = lowStockItems.filter(d => selectedIds.has(getItemKey(d)));
    try {
      for (const item of selectedItems) {
        if (item._ticket) {
          await updateTicketMut.mutateAsync({
            id: item._ticket.id,
            data: { qty_to_buy: parsed }
          });
        } else {
          await base44.entities.PurchasingTicket.create({
            material_id: item.id,
            material_type: item._type,
            material_name: item.nama,
            qty_to_buy: parsed,
            status: 'Harus Dibeli',
            vendor: item.vendor || null
          });
        }
      }
      queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] });
      setQtyInputs(prev => {
        const next = { ...prev };
        selectedItems.forEach(item => {
          next[getItemKey(item)] = { qty: parsed, confirmed: true };
        });
        return next;
      });
      toast.success(`Berhasil mengisi ${selectedItems.length} item dengan qty ${parsed}`);
      setBatchFillQty('');
    } catch (err) {
      toast.error('Gagal mengisi qty: ' + (err.message || 'Error'));
    }
  };

  const handleManualSubmit = async () => {
    if (!manualCategory || !manualItem || !manualQty) {
      toast.error('Mohon lengkapi semua data wajib!');
      return;
    }
    const isBahanCairGroup = ['bibit', 'alkohol', 'aqua-des', 'dpg', 'peg', 'sustain', 'quidex', 'castor-oil', 'lainnya', 'bahan-cair-lainnya', 'bahan-cair'].includes(manualCategory);
    if (isBahanCairGroup && (!manualVendor || manualVendor === 'all_vendors')) {
      toast.error('Vendor/Supplier wajib dipilih untuk Bahan Cair!');
      return;
    }
    
    setIsSubmittingManual(true);
    try {
      let selectedItemObj = null;
      if (manualCategory === 'botol') selectedItemObj = botolList.find(i => String(i.id) === manualItem);
      else if (manualCategory === 'tutup') selectedItemObj = tutupList.find(i => String(i.id) === manualItem);
      else if (manualCategory === 'spray') selectedItemObj = sprayList.find(i => String(i.id) === manualItem);
      else if (['bibit', 'alkohol', 'aqua-des', 'dpg', 'peg', 'sustain', 'quidex', 'castor-oil', 'lainnya', 'bahan-cair-lainnya', 'bahan-cair'].includes(manualCategory)) selectedItemObj = bahanCairList.find(i => String(i.id) === manualItem);
      else if (manualCategory === 'fullset') selectedItemObj = fullsetList.find(i => String(i.id) === manualItem);
      
      const actualMaterialType = ['bibit', 'alkohol', 'aqua-des', 'dpg', 'peg', 'sustain', 'quidex', 'castor-oil', 'lainnya', 'bahan-cair-lainnya', 'bahan-cair'].includes(manualCategory) ? 'bahan-cair' : manualCategory;

      if (!selectedItemObj) {
        toast.error('Item tidak valid');
        return;
      }

      await base44.entities.PurchasingTicket.create({
        material_id: selectedItemObj.id,
        material_type: actualMaterialType,
        material_name: selectedItemObj.nama,
        qty_to_buy: parseFloat(manualQty),
        status: 'Harus Dibeli',
        vendor: (manualVendor && manualVendor !== 'all_vendors') ? manualVendor : (selectedItemObj.vendor || null),
        is_manual: true,
        source_page: 'low_stock'
      });

      queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] });
      
      toast.success('Berhasil menambahkan item manual ke daftar stok habis/menipis');
      setShowManualModal(false);
      setManualCategory('');
      setManualVendor('');
      setManualItem('');
      setManualQty('');
    } catch (err) {
      toast.error('Gagal menambahkan item: ' + (err.message || 'Error'));
    } finally {
      setIsSubmittingManual(false);
    }
  };

  // ── Open Pengajuan Modal ──
  const applyPemesan = (fakturStatus) => {
    if (!dataPemesanList || dataPemesanList.length === 0) return;
    let targetKeyword = '';
    if (fakturStatus === 'Ya') targetKeyword = 'satala dermatech essential';
    else if (fakturStatus === 'Tidak') targetKeyword = 'apa saja bisa';
    
    if (targetKeyword) {
      const found = dataPemesanList.find(p => p.nama_perusahaan?.toLowerCase().includes(targetKeyword));
      if (found) {
        setPengajuanPemesanNama(found.nama_perusahaan || '');
        setPengajuanPemesanNPWP(found.npwp || '');
        setPengajuanPemesanAlamat(found.alamat || '');
        setPengajuanPemesanPIC(found.nama_pic || '');
        setPengajuanPemesanTelp(found.nomor_telepon || '');
        setPemesanSearch(found.nama_perusahaan || '');
      }
    }
  };

  const handleOpenPengajuan = (items, supplierData = null) => {
    setPengajuanItem(items); 
    if (supplierData) {
      setPengajuanNamaSales(supplierData.nama_sales || '');
      setPengajuanAdaFakturPajak(supplierData.ada_faktur_pajak || 'Tidak');
      setTimeout(() => applyPemesan(supplierData.ada_faktur_pajak || 'Tidak'), 50);
      setPengajuanNamaPerusahaan(supplierData.nama_perusahaan || '');
      setPengajuanNPWP(supplierData.npwp || '');
      setPengajuanBank(supplierData.bank || '');
      setPengajuanNoRek(supplierData.no_rekening || '');
      setPengajuanNamaRek(supplierData.atas_nama || '');
      setPengajuanMetode(supplierData.metode_pembayaran || 'Cash');
      setPengajuanOngkirMode(supplierData.ongkir_mode || 'transfer');
      setPengajuanSupplierPhone(supplierData.phone || '');
      setPengajuanSupplierAddress(supplierData.alamat || '');
      setSupplierSearch(supplierData.nama_sales || supplierData.nama_perusahaan || '');
    } else {
      setPengajuanNamaSales('');
      setPengajuanAdaFakturPajak('Tidak');
      setTimeout(() => applyPemesan('Tidak'), 50);
      setPengajuanNamaPerusahaan('');
      setPengajuanNPWP('');
      setPengajuanBank('BCA');
      setPengajuanNoRek('');
      setPengajuanNamaRek('');
      setPengajuanMetode('Cash');
      setPengajuanOngkirMode('transfer');
      setPengajuanSupplierPhone('');
      setPengajuanSupplierAddress('');
      setSupplierSearch('');
    }
    setPengajuanTempo('');
    setPengajuanOngkir('');
    setPengajuanCatatan('');
    setShowPengajuanModal(true);
  };

  const handleSubmitPengajuan = async () => {
    if (!pengajuanItem || pengajuanItem.length === 0) return;
    setIsSubmittingPengajuan(true);
    try {
      const isBatch = pengajuanItem.length > 1;
      const batchItemsData = pengajuanItem.map(item => {
        const buyQty = getQtyForItem(item).qty || item._deficitQty || 1;
        const isEur = item.mata_uang === 'EUR';
        const isUsd = item.mata_uang === 'USD';
        const isValas = isUsd || isEur;
        const currentRate = isEur ? (kursEuro || 17000) : (kurs || 16000);
        const valasUnit = isEur ? (parseFloat(item.harga_euro) || null) : (isUsd ? (parseFloat(item.harga_dollar) || null) : null);
        const unitPrice = (isValas && valasUnit != null && valasUnit > 0)
          ? (valasUnit * currentRate)
          : parseFloat(item.harga_rupiah || item.harga_beli || 0);

        return {
          id: item.id,
          name: item.nama,
          type: item._type,
          qty: buyQty,
          satuan: item.satuan || 'pcs',
          price: unitPrice,
          harga_rupiah: unitPrice,
          mata_uang: isEur ? 'EUR' : (isUsd ? 'USD' : 'IDR'),
          harga_dollar: isUsd ? valasUnit : null,
          harga_euro: isEur ? valasUnit : null,
          total: buyQty * unitPrice,
          ppn_type: item.ppn_type || 'include',
          ppn_percentage: item.ppn_percentage || 11,
          vendor: item.vendor || item.supplier || '',
          ticket: item._ticket ? { id: item._ticket.id, status: item._ticket.status } : null
        };
      });

      const totalNominal = batchItemsData.reduce((s, it) => s + it.total, 0);
      const totalPPN = batchItemsData.reduce((s, it) => {
        if (it.ppn_type === 'include') return s + (it.total * 11 / 111);
        if (it.ppn_type === 'exclude') return s + (it.total * 0.11);
        return s;
      }, 0);

      const hasExcludePpn = batchItemsData.some(it => it.ppn_type === 'exclude');

      const requestPayload = {
        tipe_pengajuan: isBatch ? 'kolektif' : 'tunggal',
        nama_item: isBatch ? `PO Kolektif (${batchItemsData.length} Item Menipis)` : batchItemsData[0].name,
        material_name: isBatch ? `PO Kolektif (${batchItemsData.length} Item Menipis)` : batchItemsData[0].name,
        kategori: isBatch ? 'Batch Request' : (pengajuanItem[0]._type === 'bahan-cair' ? 'Bahan Cair' : pengajuanItem[0]._type),
        material_type: isBatch ? 'batch' : batchItemsData[0].type,
        material_id: isBatch ? 'batch' : batchItemsData[0].id,
        quantity: isBatch ? batchItemsData.reduce((s, it) => s + it.qty, 0) : batchItemsData[0].qty,
        qty_beli: isBatch ? batchItemsData.reduce((s, it) => s + it.qty, 0) : batchItemsData[0].qty,
        satuan: isBatch ? 'item' : (batchItemsData[0].satuan || 'pcs'),
        harga_unit: isBatch ? 0 : batchItemsData[0].harga_rupiah,
        harga_dollar: isBatch ? null : batchItemsData[0].harga_dollar,
        harga_euro: isBatch ? null : batchItemsData[0].harga_euro,
        mata_uang: isBatch ? null : batchItemsData[0].mata_uang,
        nominal_diajukan: totalNominal,
        total_biaya: totalNominal + (parseFloat(pengajuanOngkir) || 0) + (hasExcludePpn ? totalPPN : 0),
        total_barang: totalNominal,
        nominal_ppn: totalPPN,
        ppn_nominal: totalPPN,
        ppn_status: hasExcludePpn ? 'exclude' : 'include',
        kurs_usd: pengajuanItem.some(it => it.mata_uang === 'USD' || it.harga_dollar) ? kurs : null,
        kurs_eur: pengajuanItem.some(it => it.mata_uang === 'EUR' || it.harga_euro) ? kursEuro : null,
        bank_tujuan: pengajuanBank,
        no_rekening_tujuan: pengajuanNoRek,
        nomor_rekening: pengajuanNoRek,
        atas_nama_rekening: pengajuanNamaRek,
        atas_nama: pengajuanNamaRek,
        nama_sales: pengajuanNamaSales,
        nama_perusahaan: pengajuanNamaPerusahaan,
        npwp: pengajuanNPWP,
        supplier_phone: pengajuanSupplierPhone,
        supplier_address: pengajuanSupplierAddress,
        ada_faktur_pajak: pengajuanAdaFakturPajak,
        metode_pembayaran: pengajuanMetode,
        tempo_hari: pengajuanMetode === 'Tempo' ? pengajuanTempo : null,
        tanggal_jatuh_tempo: pengajuanMetode === 'Tempo' ? pengajuanTempo : null,
        ongkir_mode: pengajuanOngkirMode,
        ongkir: parseFloat(pengajuanOngkir) || 0,
        ongkir_nominal: parseFloat(pengajuanOngkir) || 0,
        catatan: pengajuanCatatan,
        status: pengajuanMetode === 'Tempo' ? 'Sudah Dibayar' : 'Dalam Permintaan',
        batch_items: JSON.stringify(batchItemsData),
        source_page: 'low_stock',
        created_date: new Date().toISOString()
      };

      await base44.entities.PurchasingRequest.create(requestPayload);

      const nextStatus = pengajuanMetode === 'Tempo' ? 'Sudah Dibayar' : 'Dalam Permintaan';
      for (const it of batchItemsData) {
        if (it.ticket?.id) {
          await updateTicketMut.mutateAsync({
            id: it.ticket.id,
            data: { status: nextStatus, qty_to_buy: it.qty }
          });
        } else {
          await base44.entities.PurchasingTicket.create({
            material_id: it.id,
            material_type: it.type,
            material_name: it.name,
            qty_to_buy: it.qty,
            status: nextStatus,
            vendor: it.vendor || null
          });
        }
      }

      queryClient.invalidateQueries({ queryKey: ['purchasing-requests'] });
      queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] });

      toast.success('Pengajuan pembelian item stok menipis berhasil diajukan!');
      setShowPengajuanModal(false);
      clearSelection();
    } catch (err) {
      toast.error('Gagal mengajukan pembelian: ' + (err.message || 'Error'));
    } finally {
      setIsSubmittingPengajuan(false);
    }
  };

  const handleExportPDF = () => {
    exportToPDF({
      title: 'Laporan Item Stok Habis & Menipis',
      subtitle: `Total ${lowStockItems.length} item terdeteksi membutuhkan pengadaan segera`,
      filename: 'Item-Stok-Habis-Menipis',
      columns: [
        { key: 'nama', label: 'Nama Item' },
        { key: '_type', label: 'Kategori', render: r => r.kategori || r._type },
        { key: 'vendor', label: 'Vendor / Supplier', render: r => r.vendor || '-' },
        { key: 'stok', label: 'Stok Gudang', render: r => `${fmtDecimal(r.stok)} ${r.satuan}` },
        { key: '_threshold', label: 'Batas Minimum', render: r => r._threshold ? `${r._threshold} ${r.satuan}` : '-' },
        { key: '_deficitQty', label: 'Rekomendasi Beli', render: r => `${fmtDecimal(r._deficitQty)} ${r.satuan}` },
        { key: 'harga_rupiah', label: 'Harga Satuan', render: r => `Rp ${fmt(r.harga_rupiah || r.harga_beli || 0)}` },
        { key: 'total_est', label: 'Estimasi Biaya', render: r => `Rp ${fmt((r._deficitQty || 0) * (r.harga_rupiah || r.harga_beli || 0))}` }
      ],
      rows: lowStockItems
    });
  };

  const asaInsights = useMemo(() => {
    const habisTotal = lowStockItems.filter(i => Number(i.stok || 0) <= 0).length;
    const menipis = lowStockItems.filter(i => Number(i.stok || 0) > 0).length;
    const insights = [];
    if (habisTotal > 0) {
      insights.push({
        type: 'danger',
        text: `Perhatian: Terdapat ${habisTotal} item bahan/kemasan yang stoknya 0 (Habis Total). Segera buatkan PO pengadaan agar operasional produksi tidak terhambat!`
      });
    }
    if (menipis > 0) {
      insights.push({
        type: 'warning',
        text: `Info Stok: Sebanyak ${menipis} item berada di bawah batas minimum (Alkohol < 20L, Bahan Cair < 1kg/1L, Kemasan < 100pcs). Pertimbangkan untuk melakukan reorder.`
      });
    }
    return insights;
  }, [lowStockItems]);

  // ── Render Item Card (Identik dengan Item Dibutuhkan PO) ──
  const renderItemCard = (item, idx, groupSupplier = null) => {
    const key = getItemKey(item);
    const qtyData = getQtyForItem(item);
    const status = item._ticket?.status || 'Harus Dibeli';
    const matchingReq = requests.find(r => isRequestMatchingItem(r, item.id, item._type));
    
    let paymentBadge = null;
    if (matchingReq && status !== 'Harus Dibeli') {
      if (matchingReq.metode_pembayaran === 'Tempo') {
        paymentBadge = <Badge className="text-[9px] bg-orange-100 text-orange-700 border-orange-200 shadow-none">Tempo</Badge>;
      } else {
        if (matchingReq.status === 'Sudah Dibayar') {
          paymentBadge = <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200 shadow-none">Sudah Dibayar</Badge>;
        } else {
          paymentBadge = <Badge className="text-[9px] bg-rose-100 text-rose-700 border-rose-200 shadow-none">Belum Dibayar</Badge>;
        }
      }
    }
    const displayQty = qtyData.qty;
    const numericDisplayQty = Number(String(displayQty).replace(',', '.')) || 0;
    
    // Determine valas / dollar info and realtime calculation
    const isEur = item.mata_uang === 'EUR';
    const isUsd = item.mata_uang === 'USD';
    const isValas = isUsd || isEur;
    const valasSymbol = isEur ? '€' : '$';
    const valasUnit = isEur ? (parseFloat(item.harga_euro) || null) : (isUsd ? (parseFloat(item.harga_dollar) || null) : null);
    const currentRate = isEur ? (kursEuro || 17000) : (kurs || 16000);
    const unitPriceRupiah = (isValas && valasUnit != null && valasUnit > 0)
      ? (valasUnit * currentRate)
      : (parseFloat(item.harga_rupiah || item.harga_beli || 0));
    const totalCost = numericDisplayQty * unitPriceRupiah;
    const totalValas = (isValas && valasUnit != null) ? (numericDisplayQty * valasUnit) : null;
    const isSelected = selectedIds.has(key);
    const isConfirmed = qtyData.confirmed;
    const isStokHabis = Number(item.stok || 0) <= 0;

    return (
      <Card 
        key={`${key}-${idx}`} 
        onClick={() => { if (isSelectionMode) toggleSelection(item); }}
        className={`border overflow-hidden flex flex-col transition-all ${isSelectionMode ? 'cursor-pointer hover:ring-2 hover:ring-[#8C6208]/30' : ''} ${
          isSelected 
            ? 'border-[#8C6208] shadow-md ring-2 ring-[#8C6208]/20' 
            : 'border-slate-200/80 hover:border-[#8C6208]/40 hover:shadow-md'
        } bg-white`}
      >
        <CardContent className="p-0 flex flex-col h-full pointer-events-auto">
          {/* Top Status Banner + Checkbox + Dismiss */}
          <div className={`px-4 py-2 flex justify-between items-center border-b border-slate-100 ${status === 'Harus Dibeli' ? 'bg-rose-50/50' : 'bg-slate-50/50'}`}>
            <div className="flex items-center gap-2" onClick={(e) => { if (isSelectionMode) e.stopPropagation(); }}>
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => toggleSelection(item)}
                className="border-slate-300 data-[state=checked]:bg-[#8C6208] data-[state=checked]:border-[#8C6208] pointer-events-auto"
              />
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Status:</span>
            </div>
            <div className="flex items-center gap-1.5" onClick={(e) => { if (isSelectionMode) e.stopPropagation(); }}>
              <div className="relative">
                <select 
                  value={status}
                  onChange={(e) => handleStatusChange(item, e.target.value)}
                  disabled={isSelectionMode}
                  className={`appearance-none cursor-pointer outline-none text-[10px] font-black uppercase tracking-wider px-3 py-1 pr-7 rounded-full border shadow-sm transition-colors ${getStatusColor(status)} ${isSelectionMode ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <ChevronDown className="w-3 h-3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-50" />
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleIgnoreItem(item);
                }}
                title="Hapus / Abaikan dari daftar"
                className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="p-4 flex-1">
            {/* Name & Type */}
            <div 
              className={`flex gap-3 mb-3 ${isSelectionMode ? '' : 'cursor-pointer'}`} 
              onClick={(e) => {
                if (!isSelectionMode) navigate(`/office/purchasing/stok-detail?id=${item.id}&tipe=${item._type}`);
              }}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center border shrink-0 ${isStokHabis ? 'bg-rose-50 border-rose-200 text-rose-500' : 'bg-amber-50 border-amber-200 text-amber-500'}`}>
                {isStokHabis ? <AlertTriangle className="w-6 h-6" /> : <Package2 className="w-6 h-6" />}
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="font-bold text-slate-800 text-base truncate">{item.nama}</h4>
                <div className="flex flex-wrap gap-1 mt-1">
                  <Badge variant="outline" className="text-[9px] uppercase bg-slate-50">{item._type === 'bahan-cair' ? 'BAHAN CAIR' : item._type}</Badge>
                  {item._type === 'botol' && item.ukuran_label_ml && <Badge variant="outline" className="text-[9px] bg-slate-50">{item.ukuran_label_ml}ml</Badge>}
                  {item._type === 'spray' && item.tipe && <Badge className="text-[9px] bg-green-50 text-green-700 border-green-200">{item.tipe}</Badge>}
                  {item._type === 'bahan-cair' && item.kategori && <Badge className="text-[9px] bg-purple-50 text-purple-700 border-purple-200">{item.kategori}</Badge>}
                  {/* Currency Badge */}
                  {isEur ? (
                    <Badge className="text-[9px] font-black bg-blue-50 text-blue-700 border border-blue-300 shadow-none">EUR (€)</Badge>
                  ) : isUsd ? (
                    <Badge className="text-[9px] font-black bg-amber-50 text-[#8C6208] border border-amber-300 shadow-none">USD ($)</Badge>
                  ) : (
                    <Badge className="text-[9px] font-black bg-emerald-50 text-emerald-800 border border-emerald-300 shadow-none">IDR (Rp)</Badge>
                  )}
                  {isStokHabis ? (
                    <Badge className="text-[9px] bg-rose-100 text-rose-700 border-rose-200">Stok Habis (0)</Badge>
                  ) : (
                    <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200">Menipis (&lt;{item._threshold} {item.satuan})</Badge>
                  )}
                  {item.ppn_type === 'exclude' ? (
                    <Badge className="text-[9px] bg-rose-50 text-rose-700 border-rose-200 shadow-none font-bold">Exclude PPN</Badge>
                  ) : (
                    <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 shadow-none font-bold">Include PPN</Badge>
                  )}
                  {isValas && valasUnit != null && (
                    <Badge className="text-[9px] bg-amber-50 text-amber-800 border-amber-300 shadow-none font-bold">
                      {valasSymbol}{fmt(valasUnit)}/{item.satuan || 'pcs'} <span className="opacity-75 font-normal text-[8px] ml-0.5">(Rate: Rp {fmt(currentRate)})</span>
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Source Label */}
            <div className="mt-2 text-[10px] text-slate-500 bg-slate-50/50 px-2.5 py-1.5 rounded-lg border border-slate-100 leading-tight flex gap-1.5 items-center">
              <div className="shrink-0 text-[#8C6208]">
                {item._isManualInput || item._ticket?.is_manual ? <PenTool className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
              </div>
              <div>
                <span className="font-bold text-slate-700">
                  {item._isManualInput || item._ticket?.is_manual ? 'Input Manual' : `Batas Minimum: ${item._threshold} ${item.satuan}`}
                </span>
              </div>
            </div>

            {/* Compact PO Progress Bar */}
            {(() => {
              const statusStr = item._ticket?.status || status || 'Harus Dibeli';
              let currentStep = 1;
              if (['Dalam Permintaan', 'Menunggu Pembayaran', 'Sudah Dibayar'].includes(statusStr)) currentStep = 2;
              else if (statusStr === 'Dalam Pengiriman') currentStep = 3;
              else if (statusStr === 'Sudah Datang') currentStep = 4;

              const stepLabels = ['Perlu Beli', 'Naik PO Supplier', 'Dalam Pengiriman', 'Tiba di Gudang'];

              return (
                <div className="mt-2.5 mb-2 flex items-center justify-between bg-[#F9F5EC] px-3 py-1.5 rounded-xl border border-[#E6D5B8] text-[10px]">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Clock className="w-3.5 h-3.5 text-[#8C6208] shrink-0" />
                    <span className="font-bold text-slate-700 truncate">Progres PO:</span>
                    <span className="font-black text-[#8C6208] truncate">{currentStep}/4 ({stepLabels[currentStep-1]})</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedProgressItem({ ...item, _currentStep: currentStep, _statusStr: statusStr, _matchingReq: matchingReq }); }}
                    className="font-black text-[#8C6208] hover:underline flex items-center gap-0.5 shrink-0 ml-2"
                  >
                    Lacak Detail <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              );
            })()}

            {/* Stock Info */}
            <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 rounded-lg p-3">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase">Stok Gudang</p>
                <p className={`font-semibold ${(item.stok || 0) <= 0 ? 'text-rose-600' : 'text-slate-700'}`}>
                  {fmtDecimal(item.stok || 0)} {item.satuan}
                </p>
              </div>
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase">Batas Minimum</p>
                <p className="font-semibold text-slate-700">{item._threshold ? `${item._threshold} ${item.satuan}` : '-'}</p>
              </div>
            </div>
            
            {/* ── Qty Input Section ── */}
            <div 
              className="mt-3 border rounded-xl p-3 bg-rose-50/50 border-rose-100"
              onClick={(e) => { if (isSelectionMode) e.stopPropagation(); }}
            >
              <p className="text-[9px] font-bold uppercase flex items-center gap-1 mb-2 text-rose-500">
                <AlertTriangle className="w-3 h-3" /> Harus Dibeli
              </p>

              <div className="flex items-center gap-2">
                {isConfirmed ? (
                  <div className="flex items-center gap-2 flex-1">
                    <div className="flex-1">
                      <p className="font-black text-xl text-rose-700">
                        {fmtDecimal(displayQty)} <span className="text-xs font-bold text-rose-500">{item.satuan}</span>
                      </p>
                    </div>
                    <button
                      onClick={() => { if (!isSelectionMode) editQtyForItem(item); }}
                      disabled={isSelectionMode}
                      className={`p-1.5 rounded-lg bg-white border border-slate-200 text-slate-500 transition-colors ${isSelectionMode ? 'opacity-50 cursor-not-allowed' : 'hover:text-[#8C6208] hover:border-[#8C6208]/30'}`}
                      title="Edit jumlah"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <div className="flex-1 relative">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={displayQty}
                        onChange={(e) => setQtyForItem(item, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') confirmQtyForItem(item); }}
                        disabled={isSelectionMode}
                        className={`h-10 text-lg font-bold pr-12 border-2 rounded-xl border-rose-300 focus:border-rose-500 ${isSelectionMode ? 'opacity-50' : ''}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-rose-400">{item.satuan}</span>
                    </div>
                    <button
                      onClick={() => { if (!isSelectionMode) confirmQtyForItem(item); }}
                      disabled={isSelectionMode}
                      className={`p-2 rounded-xl text-white transition-colors shadow-sm bg-rose-500 hover:bg-rose-600 ${isSelectionMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title="Konfirmasi jumlah"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Est. Biaya */}
              {showPrice && (
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed border-slate-200/60">
                  <span className="text-[9px] font-bold uppercase text-rose-400">Est. Biaya</span>
                  <div className="text-right">
                    {totalValas != null && (
                      <span className="text-[11px] font-extrabold text-amber-700 mr-1.5 inline-block">
                        {valasSymbol} {fmt(totalValas)} <span className="opacity-60 text-slate-400 font-normal">≈</span>
                      </span>
                    )}
                    <span className="font-bold text-sm text-rose-700">Rp {fmt(totalCost)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 mt-auto transition-colors">
            <div className="flex items-center gap-3">
              <button 
                type="button"
                className="text-[10px] text-[#8C6208] font-black cursor-pointer hover:bg-amber-100/80 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200 flex items-center gap-1 transition-all shadow-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedProgressItem(item);
                }}
              >
                <Clock className="w-3.5 h-3.5 text-[#8C6208]" /> Progres PO
              </button>
              <span 
                className="text-[10px] text-slate-500 font-medium cursor-pointer hover:text-slate-700 flex items-center gap-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isSelectionMode) navigate(`/office/purchasing/stok-detail?id=${item.id}&tipe=${item._type}`);
                }}
              >
                Detail Stok <ChevronRight className="w-3 h-3" />
              </span>
              
              {item._ticket && !isSelectionMode && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (window.confirm('Apakah Anda yakin ingin menghapus tiket ini?')) {
                      try {
                        await deleteTicketMut.mutateAsync(item._ticket.id);
                        toast.success('Item berhasil dihapus');
                      } catch(err) {
                        toast.error('Gagal menghapus item');
                      }
                    }
                  }}
                  className="flex items-center gap-1 text-[10px] text-rose-500 font-medium hover:text-rose-700 transition-colors bg-white px-2 py-0.5 rounded border border-rose-100 shadow-sm"
                >
                  <Trash2 className="w-3 h-3" /> Hapus
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {status === 'Harus Dibeli' && !isSelectionMode && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleOpenPengajuan([item], groupSupplier); }}
                  className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-xl bg-[#8C6208] hover:bg-[#6B4A06] text-white transition-colors shadow-sm"
                >
                  <Send className="w-3 h-3" /> Pengajuan
                </button>
              )}
              {(status === 'Dalam Permintaan') && (
                paymentBadge ? paymentBadge : <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200 shadow-none">Menunggu Finance</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 text-[#302621] print:hidden">
      <div>
        <h1 className="text-3xl font-black text-slate-800 tracking-tight">Item Perlu Beli</h1>
        <p className="text-xs text-slate-500 mt-1">
          Daftar bahan baku & kemasan di gudang yang menipis/habis dan tidak sedang dibutuhkan PO aktif (khusus restock / buffer stock).
        </p>
      </div>

      {asaInsights.length > 0 && showAsa && (
        <AsaAssistant 
          insights={asaInsights}
          title="Rekomendasi Asa"
          theme="indigo"
        />
      )}

      {/* ── BATCH ACTION BAR ── */}
      {isSelectionMode && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-[95%] max-w-3xl bg-white/95 shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-slate-200/60 rounded-2xl p-3 animate-in slide-in-from-top-4 fade-in duration-300">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button onClick={selectAll} className="flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-slate-900">
                {selectedIds.size === lowStockItems.length ? <CheckSquare className="w-5 h-5 text-[#8C6208]" /> : <Square className="w-5 h-5 text-slate-400" />}
                <span className="hidden sm:inline">Pilih Semua</span>
              </button>
              <div className="h-6 w-px bg-slate-200" />
              <span className="text-sm font-black text-[#8C6208] bg-amber-50 px-3 py-1 rounded-lg border border-amber-200">
                {selectedIds.size} Terpilih
              </span>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1">
                <Input
                  type="number"
                  placeholder="Isi qty..."
                  value={batchFillQty}
                  onChange={e => setBatchFillQty(e.target.value)}
                  className="w-24 h-7 text-xs border-0 bg-transparent p-0 focus-visible:ring-0"
                />
                <Button 
                  size="sm" 
                  onClick={handleBatchFillQty} 
                  disabled={!batchFillQty}
                  className="h-6 px-2 text-[10px] bg-[#8C6208] hover:bg-[#6B4A06] text-white rounded-lg"
                >
                  Isi Semua
                </Button>
              </div>

              <Button 
                onClick={() => {
                  const itemsToBuy = lowStockItems.filter(d => selectedIds.has(getItemKey(d)));
                  if (itemsToBuy.length > 0) {
                    const firstItemKey = getItemKey(itemsToBuy[0]);
                    const group = supplierGrouped.find(g => g.items.some(it => getItemKey(it) === firstItemKey));
                    handleOpenPengajuan(itemsToBuy, group?.supplier || null);
                  }
                }}
                className="h-8 px-3 text-[10px] font-black uppercase tracking-wider bg-[#8C6208] hover:bg-[#6B4A06] text-white rounded-xl shadow-sm gap-1 transition-all"
              >
                <FileText className="w-3.5 h-3.5" />
                Buat PO
              </Button>

              <Button onClick={clearSelection} variant="ghost" size="icon" className="h-8 w-8 rounded-xl hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {lowStockItems.length === 0 ? (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-3xl">
          <CheckCircle className="w-12 h-12 mx-auto text-emerald-400 mb-3" />
          <p className="text-sm font-semibold text-slate-500">Semua stok aman!</p>
          <p className="text-xs text-slate-400 mt-1">Tidak ada item bahan atau kemasan yang berada di bawah batas minimum.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* ── SOURCE FILTER TABS ── */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setSelectedSourceFilter('all')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all border whitespace-nowrap flex items-center gap-2 ${
                selectedSourceFilter === 'all'
                  ? 'bg-[#302621] text-white border-[#302621] shadow-xs'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>Semua Stok Menipis</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${selectedSourceFilter === 'all' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                {sourceCounts.total}
              </span>
            </button>

            <button
              onClick={() => setSelectedSourceFilter('low_stock')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all border whitespace-nowrap flex items-center gap-2 ${
                selectedSourceFilter === 'low_stock'
                  ? 'bg-rose-700 text-white border-rose-700 shadow-xs'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Habis & Menipis Otomatis</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${selectedSourceFilter === 'low_stock' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                {sourceCounts.lowStock}
              </span>
            </button>

            <button
              onClick={() => setSelectedSourceFilter('manual')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all border whitespace-nowrap flex items-center gap-2 ${
                selectedSourceFilter === 'manual'
                  ? 'bg-slate-700 text-white border-slate-700 shadow-xs'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              <PenTool className="w-3.5 h-3.5" />
              <span>Input Manual</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${selectedSourceFilter === 'manual' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                {sourceCounts.manual}
              </span>
            </button>
          </div>

          {/* ── FILTERS & SEARCH ── */}
          <div className="flex flex-col lg:flex-row gap-3 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 items-center flex-1 w-full">
              <div className="relative flex-1 w-full">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Cari nama item, kategori, atau vendor..."
                  value={mainSearchQuery}
                  onChange={(e) => setMainSearchQuery(e.target.value)}
                  className="pl-10 h-10 border-slate-200 bg-white rounded-xl text-xs font-medium"
                />
              </div>
              <div className="w-full sm:w-[260px]">
                <Select value={selectedSupplierFilter} onValueChange={setSelectedSupplierFilter}>
                  <SelectTrigger className="h-10 border-slate-200 bg-white rounded-xl text-xs font-bold text-slate-700">
                    <SelectValue placeholder="Pilih Supplier / Vendor..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="all" className="font-bold">Semua Supplier & Vendor</SelectItem>
                    {supplierGrouped.map(group => (
                      <SelectItem key={group.key} value={group.key} className="font-medium text-xs">
                        {group.label} ({group.items.length})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2 w-full lg:w-auto">
              <Button 
                onClick={() => {
                  setShowManualModal(true);
                  setManualCategory('');
                  setManualVendor('');
                  setManualItem('');
                  setManualQty('');
                }}
                className="bg-[#8C6208] hover:bg-[#6B4A06] text-white shadow-sm font-bold uppercase tracking-wider text-[10px] rounded-xl h-10 px-4 whitespace-nowrap"
              >
                + Tambah Item Manual
              </Button>
            </div>
          </div>

          {/* ── Render Supplier-Grouped Items ── */}
          {filteredSupplierGrouped.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((group) => {
            const harusBeliItems = group.items.filter(it => (it._ticket?.status || 'Harus Dibeli') === 'Harus Dibeli');
            const totalEstimasi = group.items.reduce((s, it) => s + ((getQtyForItem(it).qty || it._deficitQty || 0) * (it.harga_rupiah || it.harga_beli || 0)), 0);

            return (
              <div key={group.key} className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
                <div className="bg-[#302621] text-white px-5 py-3.5 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                      <Building2 className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-sm font-black tracking-tight">{group.label}</h2>
                      </div>
                      <p className="text-[10px] text-slate-300 font-medium">
                        {group.items.length} ITEM • EST. RP {fmt(totalEstimasi)}
                      </p>
                    </div>
                  </div>
                  {harusBeliItems.length > 0 && !isSelectionMode && (
                    <Button
                      onClick={() => handleOpenPengajuan(harusBeliItems, group.supplier)}
                      className="bg-[#D4AF37] hover:bg-[#C29D29] text-[#302621] font-black uppercase tracking-wider text-[10px] h-8 px-3 rounded-xl shadow-sm gap-1 transition-all"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Buat PO ({harusBeliItems.length})
                    </Button>
                  )}
                </div>

                <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-slate-50/40">
                  {group.items.map((item, idx) => renderItemCard(item, idx, group.supplier))}
                </div>
              </div>
            );
          })}

          {/* ── Pagination Controls ── */}
          {filteredSupplierGrouped.length > 0 && (
            <div className="px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-500">Supplier per Halaman</span>
                <div className="w-[80px]">
                  <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25, 50].map(n => (
                        <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 transition-all font-bold text-sm"
                >
                  &lt;
                </button>
                <span className="text-xs font-bold text-slate-600 px-2">
                  Halaman {currentPage} dari {Math.max(1, Math.ceil(filteredSupplierGrouped.length / itemsPerPage))}
                </span>
                <button 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredSupplierGrouped.length / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(filteredSupplierGrouped.length / itemsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 transition-all font-bold text-sm"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── DETAIL PROGRESS TRACKING MODAL ── */}
      {selectedProgressItem && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-[#302621] text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center text-[#D4AF37]">
                  <Package2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-black truncate max-w-[280px]">{selectedProgressItem.nama}</h3>
                  <p className="text-[10px] text-slate-300 font-bold uppercase tracking-wider">{selectedProgressItem.kategori || selectedProgressItem._type}</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedProgressItem(null)} 
                className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6">
              <div className="bg-amber-50/70 p-4 rounded-2xl border border-amber-200/60 space-y-2.5">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-slate-600">Stok Gudang:</span>
                  <span className="font-black text-[#8C6208] text-sm">{fmtDecimal(selectedProgressItem.stok)} {selectedProgressItem.satuan}</span>
                </div>
                <div className="flex justify-between items-center text-xs border-t border-amber-200/50 pt-2">
                  <span className="font-bold text-slate-600">Batas Minimum:</span>
                  <span className="font-black text-rose-700">{selectedProgressItem._threshold ? `${selectedProgressItem._threshold} ${selectedProgressItem.satuan}` : '-'}</span>
                </div>
                <div className="text-xs border-t border-amber-200/50 pt-2">
                  <span className="font-bold text-slate-700 block mb-1">Sumber: <span className="text-[#8C6208]">{selectedProgressItem._isManualInput ? 'Input Manual' : 'Batas Minimum Habis/Menipis'}</span></span>
                </div>
              </div>

              {/* 4 Step Timeline */}
              <div className="space-y-4">
                <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider">Tahapan Pengadaan</h4>
                <div className="relative pl-6 space-y-6 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
                  {[
                    { step: 1, title: 'Item Terdeteksi Menipis / Butuh Beli', desc: 'Item masuk daftar harus dibeli' },
                    { step: 2, title: 'Pengajuan PO Supplier Diajukan', desc: 'Menunggu konfirmasi pembayaran Finance' },
                    { step: 3, title: 'Dalam Pengiriman Supplier', desc: 'Barang sedang menuju gudang Satala' },
                    { step: 4, title: 'Barang Tiba di Gudang', desc: 'Stok gudang otomatis bertambah' }
                  ].map((s) => {
                    const isDone = (selectedProgressItem._currentStep || 1) >= s.step;
                    return (
                      <div key={s.step} className="relative flex items-start gap-3">
                        <div className={`absolute -left-6 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ring-4 ring-white ${isDone ? 'bg-[#8C6208] text-white' : 'bg-slate-200 text-slate-500'}`}>
                          {s.step}
                        </div>
                        <div>
                          <p className={`text-xs font-bold ${isDone ? 'text-slate-800' : 'text-slate-400'}`}>{s.title}</p>
                          <p className="text-[10px] text-slate-500">{s.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
              <Button onClick={() => setSelectedProgressItem(null)} className="bg-[#302621] text-white text-xs font-bold rounded-xl h-9">
                Tutup
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── PENGAJUAN PEMBELIAN MODAL ── */}
      {showPengajuanModal && pengajuanItem && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50 to-amber-50/30">
              <div>
                <h3 className="text-lg font-black text-slate-800">
                  {pengajuanItem.length > 1 ? `Pengajuan PO Kolektif (${pengajuanItem.length} Item)` : `Pengajuan PO: ${pengajuanItem[0].nama}`}
                </h3>
                <p className="text-xs text-slate-500 font-medium">Buat permohonan dana pembelian item menipis ke Finance</p>
              </div>
              <button onClick={() => setShowPengajuanModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4">
              <div className="bg-amber-50/50 p-4 rounded-2xl border border-amber-100">
                <span className="text-xs font-bold text-amber-900 block mb-2">Item yang Diajukan:</span>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {pengajuanItem.map((it, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white p-2 rounded-xl text-xs border border-amber-100/50">
                      <span className="font-bold text-slate-700">{it.nama}</span>
                      <span className="font-black text-[#8C6208]">{fmtDecimal(getQtyForItem(it).qty || it._deficitQty)} {it.satuan}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase mb-1 block">Nama Sales / Supplier</label>
                  <Input value={pengajuanNamaSales} onChange={e => setPengajuanNamaSales(e.target.value)} placeholder="Contoh: Budi (PT ABC)" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase mb-1 block">Nama Perusahaan Vendor</label>
                  <Input value={pengajuanNamaPerusahaan} onChange={e => setPengajuanNamaPerusahaan(e.target.value)} placeholder="Nama PT / CV" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase mb-1 block">Bank Tujuan</label>
                  <Input value={pengajuanBank} onChange={e => setPengajuanBank(e.target.value)} placeholder="BCA / Mandiri / BNI" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase mb-1 block">No. Rekening</label>
                  <Input value={pengajuanNoRek} onChange={e => setPengajuanNoRek(e.target.value)} placeholder="Nomor rekening tujuan" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase mb-1 block">Atas Nama Rekening</label>
                  <Input value={pengajuanNamaRek} onChange={e => setPengajuanNamaRek(e.target.value)} placeholder="Nama pemilik rekening" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase mb-1 block">Metode Pembayaran</label>
                  <Select value={pengajuanMetode} onValueChange={setPengajuanMetode}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash / Transfer</SelectItem>
                      <SelectItem value="Tempo">Tempo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {pengajuanMetode === 'Tempo' && (
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase mb-1 block">Jatuh Tempo (Hari / Tanggal)</label>
                  <Input value={pengajuanTempo} onChange={e => setPengajuanTempo(e.target.value)} placeholder="Contoh: 30 Hari atau 2026-09-30" />
                </div>
              )}

              <div>
                <label className="text-xs font-bold text-slate-700 uppercase mb-1 block">Catatan Tambahan</label>
                <Input value={pengajuanCatatan} onChange={e => setPengajuanCatatan(e.target.value)} placeholder="Catatan untuk Finance..." />
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 rounded-b-3xl">
              <Button variant="outline" onClick={() => setShowPengajuanModal(false)}>Batal</Button>
              <Button onClick={handleSubmitPengajuan} disabled={isSubmittingPengajuan} className="bg-[#8C6208] hover:bg-[#6B4A06] text-white font-bold">
                {isSubmittingPengajuan ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Kirim Pengajuan ke Finance'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── TAMBAH ITEM MANUAL MODAL ── */}
      {showManualModal && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-base font-black text-slate-800">Tambah Item Manual</h3>
                <p className="text-xs text-slate-500">Tambahkan kebutuhan pengadaan material secara manual</p>
              </div>
              <button onClick={() => setShowManualModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase mb-1 block">Kategori</label>
                <Select value={manualCategory} onValueChange={(v) => { setManualCategory(v); setManualItem(''); }}>
                  <SelectTrigger><SelectValue placeholder="Pilih Kategori..." /></SelectTrigger>
                  <SelectContent className="z-[300]">
                    <SelectItem value="bibit">Bibit</SelectItem>
                    <SelectItem value="alkohol">Alkohol</SelectItem>
                    <SelectItem value="aqua-des">Aqua Des</SelectItem>
                    <SelectItem value="dpg">DPG</SelectItem>
                    <SelectItem value="peg">PEG</SelectItem>
                    <SelectItem value="sustain">Sustain</SelectItem>
                    <SelectItem value="quidex">Quidex</SelectItem>
                    <SelectItem value="castor-oil">Castor Oil</SelectItem>
                    <SelectItem value="lainnya">Lainnya (Bahan Cair)</SelectItem>
                    <SelectItem value="botol">Botol</SelectItem>
                    <SelectItem value="tutup">Tutup</SelectItem>
                    <SelectItem value="spray">Spray</SelectItem>
                    <SelectItem value="fullset">Fullset</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {manualCategory && (
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase mb-1 block">Item</label>
                  <SearchableSelect
                    value={manualItem}
                    onValueChange={setManualItem}
                    placeholder="Pilih Item..."
                    options={(() => {
                      let list = [];
                      if (manualCategory === 'botol') list = botolList;
                      else if (manualCategory === 'tutup') list = tutupList;
                      else if (manualCategory === 'spray') list = sprayList;
                      else if (manualCategory === 'fullset') list = fullsetList;
                      else list = bahanCairList;

                      return list.map(item => ({
                        value: String(item.id),
                        label: `${item.nama} ${item.vendor ? '(' + item.vendor + ')' : ''} - Stok: ${fmtDecimal(item.stok)}`
                      }));
                    })()}
                  />
                </div>
              )}

              {manualItem && (
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase mb-1 block">Jumlah Beli</label>
                  <Input
                    type="number"
                    placeholder="Masukkan jumlah..."
                    value={manualQty}
                    onChange={e => setManualQty(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 rounded-b-3xl">
              <Button variant="outline" onClick={() => setShowManualModal(false)}>Batal</Button>
              <Button onClick={handleManualSubmit} disabled={isSubmittingManual} className="bg-[#8C6208] hover:bg-[#6B4A06] text-white font-bold">
                {isSubmittingManual ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Simpan Kebutuhan'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
